import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation } from '@tanstack/react-router';
import { useClient } from '@/hooks/context/useClient';
import { getBinderIdFromUrl } from '@/utils/urlContext';
import { createLogger } from '@/utils/logger';

const log = createLogger('SentryClientContext');

/**
 * SentryClientContext Component
 *
 * Sets client and binder context in Sentry when selection/navigation changes.
 * This enables Sentry to capture client ID and binder ID with error reports
 * for easier debugging.
 *
 * Place this component inside ClientProvider but outside error boundaries
 * so it can track context throughout the session.
 *
 * @example
 * ```tsx
 * <ClientProvider>
 *   <SentryUserContext />
 *   <SentryClientContext />
 *   <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
 *     <RouterProvider router={router} />
 *   </Sentry.ErrorBoundary>
 * </ClientProvider>
 * ```
 */
export function SentryClientContext() {
  const { selectedClient } = useClient();
  const location = useLocation();

  useEffect(() => {
    // Set client ID tag
    if (selectedClient?.id) {
      Sentry.setTag('client.id', selectedClient.id);
      log.debug('Sentry client.id set:', selectedClient.id);
    } else {
      Sentry.setTag('client.id', null);
      log.debug('Sentry client.id cleared');
    }
  }, [selectedClient?.id]);

  useEffect(() => {
    // Set binder ID tag based on current URL
    const binderId = getBinderIdFromUrl();

    if (binderId) {
      Sentry.setTag('binder.id', binderId);
      log.debug('Sentry binder.id set:', binderId);
    } else {
      Sentry.setTag('binder.id', null);
      log.debug('Sentry binder.id cleared');
    }
  }, [location.pathname]);

  // This component doesn't render anything
  return null;
}
